import React, { useState, useEffect, useCallback } from 'react'
import { Input, Table, Spin, Progress, Button, Empty, Segmented, Tag } from 'antd'
import { useNavigate, useLocation } from 'react-router-dom'
import styled from 'styled-components'
import AssignmentActivity from '../AssignmentActivity'
import { getSetGroups, subscribeSSE, getBroadcast } from '../../services/assessmentSetGroupAPI'
import { getAssignments } from '../../services/assignmentActivityAPI'
import adminRequest from 'utils/adminRequest'
import { Popover } from 'antd'

/* ───────── Theme tokens ───────── */
const T = {
  primary: '#3249D7',
  primaryHover: '#444CE7',
  bodyBg: '#FAFAFB',
  textPrimary: '#242E39',
  textSecondary: '#637487',
  border: '#EBECEE',
  grey92: '#F0F2F4',
  grey95: '#F2F3F5',
  grey98: '#FBFCFC',
  green: '#4cb187',
  green80: '#DFF0EA',
  orange: '#FFA642',
  red: '#DC3232',
  purpleBg: '#FBFAFF',
  purpleBorder: '#E3DEFF',
  purple: '#8246BE',
  radiusCard: '10px',
  radiusBtn: '8px',
  shadow: '0px 2px 10px rgba(99, 116, 135, 0.2)',
}

/* ───────── Quota donut ring + popover ───────── */
const QUOTA_CACHE = {}
const QUOTA_CACHE_TTL = 30000

const DonutRing = ({ size = 30, pct = 0, color = T.primary, strokeWidth = 3.5 }) => {
  const r = (size - strokeWidth * 2) / 2
  const circ = 2 * Math.PI * r
  const offset = circ - (Math.min(pct, 100) / 100) * circ
  return (
    <svg width={size} height={size} style={{ transform: 'rotate(-90deg)', flexShrink: 0 }}>
      <circle cx={size/2} cy={size/2} r={r} fill="none" stroke="#C7D0E0" strokeWidth={strokeWidth} />
      <circle cx={size/2} cy={size/2} r={r} fill="none" stroke={color} strokeWidth={strokeWidth}
        strokeDasharray={circ} strokeDashoffset={offset} strokeLinecap="round"
        style={{ transition: 'stroke-dashoffset 0.4s ease' }} />
    </svg>
  )
}

const TYPE_COLORS = {
  'role_based': '#3249D7',
  'communication': '#27AB66',
  'aptitude': '#E5780B',
  'behavior': '#8246BE',
  'custom_assessment': '#0EA5E9',
  'ai_interview': '#EC4899',
  'cognitive': '#6366F1',
  'tech_mcq': '#14B8A6',
  'tech_coding': '#F59E0B',
}

const getTypeColor = (typeName) => {
  const key = (typeName || '').toLowerCase().replace(/[\s-]+/g, '_')
  return TYPE_COLORS[key] || T.primary
}

const QuotaPopoverContent = ({ quotas }) => (
  <div style={{ minWidth: 240, maxWidth: 300, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
    <div style={{
      fontWeight: 600, fontSize: 13, color: '#242E39', marginBottom: 10,
      paddingBottom: 8, borderBottom: '1px solid #EBECEE',
    }}>
      Subscription Quota
    </div>
    {quotas.map((q, i) => {
      const pct = q.tokenLimit > 0 ? Math.round((q.tokensUsed / q.tokenLimit) * 100) : 0
      const typeColor = getTypeColor(q.typeName)
      return (
        <div key={i} style={{ padding: '8px 0', borderBottom: i < quotas.length - 1 ? '1px solid #F0F2F4' : 'none' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 5 }}>
            <span style={{ fontSize: 13, color: '#242E39', fontWeight: 500 }}>
              {q.typeName.replace(/_/g, ' ')}
            </span>
            <span style={{ fontSize: 12, color: '#637487', fontWeight: 500 }}>
              {q.tokensUsed} <span style={{ color: '#8895A3' }}>/ {q.tokenLimit}</span>
            </span>
          </div>
          <div style={{ width: '100%', height: 6, background: '#F0F2F4', borderRadius: 3, overflow: 'hidden' }}>
            <div style={{
              width: `${Math.min(pct, 100)}%`, height: '100%',
              background: pct >= 100 ? T.red : typeColor,
              borderRadius: 3, transition: 'width 0.3s',
            }} />
          </div>
        </div>
      )
    })}
  </div>
)

const QuotaDonut = ({ entityId }) => {
  const [quotas, setQuotas] = React.useState(null)
  const [loading, setLoading] = React.useState(false)

  const fetchQuotas = React.useCallback(async () => {
    if (!entityId) return
    const cached = QUOTA_CACHE[entityId]
    if (cached && Date.now() - cached.ts < QUOTA_CACHE_TTL) {
      setQuotas(cached.data)
      return
    }
    setLoading(true)
    try {
      const res = await adminRequest.get('/assessment/getInstituteSubscriptionQuota', { params: { institute_id: entityId } })
      const data = res?.data?.data
      if (data?.quotas) {
        setQuotas(data)
        QUOTA_CACHE[entityId] = { data, ts: Date.now() }
      }
    } catch (e) {}
    setLoading(false)
  }, [entityId])

  const pct = quotas ? (quotas.totalLimit > 0 ? Math.round((quotas.totalUsed / quotas.totalLimit) * 100) : 0) : 0
  const ringColor = pct >= 100 ? T.red : pct >= 80 ? '#E5780B' : '#3249D7'

  return (
    <Popover
      trigger="hover"
      placement="rightTop"
      color="#fff"
      overlayInnerStyle={{ padding: '14px 16px', borderRadius: 10, background: '#FFFFFF', boxShadow: '0 4px 24px rgba(36, 46, 57, 0.14)', border: '1px solid #EBECEE' }}
      overlayStyle={{ maxWidth: 320 }}
      onOpenChange={(open) => { if (open) fetchQuotas() }}
      content={quotas ? <QuotaPopoverContent quotas={quotas.quotas} /> :
        <div style={{ padding: 4, fontSize: 12, color: '#637487', fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
          {loading ? 'Loading quotas...' : 'No quota data'}
        </div>
      }
    >
      <div style={{ cursor: 'pointer', display: 'inline-flex', alignItems: 'center', padding: 2 }}>
        <DonutRing pct={pct} color={ringColor} />
      </div>
    </Popover>
  )
}

/* ───────── timeAgo helper ───────── */
const timeAgo = (dateStr) => {
  if (!dateStr) return ''
  const now = Date.now()
  const then = new Date(dateStr).getTime()
  const diff = Math.max(0, now - then)
  const seconds = Math.floor(diff / 1000)
  if (seconds < 60) return 'just now'
  const minutes = Math.floor(seconds / 60)
  if (minutes < 60) return `${minutes} min ago`
  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `${hours} hour${hours > 1 ? 's' : ''} ago`
  const days = Math.floor(hours / 24)
  if (days < 30) return `${days} day${days > 1 ? 's' : ''} ago`
  const months = Math.floor(days / 30)
  if (months < 12) return `${months} month${months > 1 ? 's' : ''} ago`
  const years = Math.floor(months / 12)
  return `${years} year${years > 1 ? 's' : ''} ago`
}

/* ───────── Styled components ───────── */

const PageWrapper = styled.div`
  padding: 32px 40px;
  background: ${T.bodyBg};
  min-height: 100vh;
  font-family: 'Plus Jakarta Sans', sans-serif;
`

const HeaderCard = styled.div`
  background: #fff;
  border-radius: ${T.radiusCard};
  box-shadow: ${T.shadow};
  padding: 24px 28px 0;
  margin-bottom: 24px;
`

const BreadcrumbRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 16px;
`

const BackLink = styled.span`
  font-size: 13px;
  font-weight: 500;
  color: ${T.textSecondary};
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  transition: color 0.2s;
  &:hover {
    color: ${T.primary};
  }
  svg {
    width: 14px;
    height: 14px;
  }
`

const PageTitle = styled.h2`
  font-size: 22px;
  font-weight: 700;
  color: ${T.textPrimary};
  margin: 0 0 20px;
  letter-spacing: -0.3px;
`

const TabContainer = styled.div`
  display: flex;
  gap: 0;
  border-bottom: 2px solid ${T.border};
`

const Tab = styled.div`
  padding: 10px 24px 12px;
  font-size: 14px;
  font-weight: ${(p) => (p.$active ? '600' : '500')};
  color: ${(p) => (p.$active ? T.primary : T.textSecondary)};
  border-bottom: 2px solid ${(p) => (p.$active ? T.primary : 'transparent')};
  margin-bottom: -2px;
  cursor: pointer;
  transition: all 0.25s ease;
  user-select: none;

  &:hover {
    color: ${(p) => (p.$active ? T.primary : T.textPrimary)};
  }
`

const SelectorSection = styled.div`
  margin-bottom: 24px;
`

const SelectLabel = styled.label`
  display: block;
  font-size: 12px;
  font-weight: 600;
  color: ${T.textSecondary};
  text-transform: uppercase;
  letter-spacing: 0.4px;
  margin-bottom: 8px;
`

const TableCard = styled.div`
  background: #fff;
  border-radius: ${T.radiusCard};
  box-shadow: ${T.shadow};
  padding: 4px 0;
  overflow: hidden;

  .ant-table {
    font-family: 'Plus Jakarta Sans', sans-serif;
  }

  .ant-table-thead > tr > th {
    background: ${T.grey98};
    font-size: 11px;
    font-weight: 700;
    color: ${T.textSecondary};
    text-transform: uppercase;
    letter-spacing: 0.5px;
    border-bottom: 1px solid ${T.border};
    padding: 12px 20px;
  }

  .ant-table-tbody > tr > td {
    padding: 16px 20px;
    border-bottom: 1px solid ${T.border};
    vertical-align: middle;
  }

  .ant-table-tbody > tr:last-child > td {
    border-bottom: none;
  }

  .ant-table-tbody > tr {
    transition: background 0.2s ease;
  }

  .ant-table-tbody > tr:hover > td {
    background: ${T.grey98} !important;
  }

  .ant-table-placeholder {
    border-bottom: none;
  }
`

const RoleCell = styled.div``

const RoleName = styled.div`
  font-weight: 600;
  font-size: 14px;
  color: ${T.textPrimary};
  margin-bottom: 3px;
`

const RoleMeta = styled.div`
  font-size: 12px;
  color: ${T.textSecondary};
  display: flex;
  align-items: center;
  gap: 6px;
`

const MetaDot = styled.span`
  width: 3px;
  height: 3px;
  border-radius: 50%;
  background: ${T.textSecondary};
  display: inline-block;
`

const StudentsCount = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: ${T.textPrimary};
`

const SetsCell = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`

const SetsLabel = styled.span`
  font-size: 12px;
  color: ${T.textSecondary};
  font-weight: 600;
  white-space: nowrap;
`

/* Status tag */
const StatusBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  font-weight: 600;
  padding: 4px 12px;
  border-radius: 20px;
  background: ${(p) => p.$bg};
  color: ${(p) => p.$color};
  white-space: nowrap;
`

const StatusDot = styled.span`
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: ${(p) => p.$color};
  display: inline-block;
  flex-shrink: 0;
`

const CheckIcon = () => (
  <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M2.5 6L5 8.5L9.5 3.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
)

/* Empty states */
const EmptyStateWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 80px 24px;
  text-align: center;
`

const EmptyIcon = styled.div`
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: ${T.grey92};
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 20px;

  svg {
    width: 36px;
    height: 36px;
    color: ${T.textSecondary};
  }
`

const EmptyTitle = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: ${T.textPrimary};
  margin-bottom: 8px;
`

const EmptyDesc = styled.div`
  font-size: 13px;
  color: ${T.textSecondary};
  max-width: 320px;
  line-height: 1.5;
`

/* Action buttons */
const ActionBtn = styled(Button)`
  border-radius: ${T.radiusBtn} !important;
  font-weight: 600 !important;
  font-size: 12px !important;
  height: 32px !important;
  padding: 0 16px !important;
  font-family: 'Plus Jakarta Sans', sans-serif !important;
  transition: all 0.2s ease !important;
`

const PrimaryBtn = styled(ActionBtn)`
  background: ${T.primary} !important;
  border-color: ${T.primary} !important;
  color: #fff !important;
  &:hover {
    background: ${T.primaryHover} !important;
    border-color: ${T.primaryHover} !important;
  }
`

const GreenBtn = styled(ActionBtn)`
  background: ${T.green} !important;
  border-color: ${T.green} !important;
  color: #fff !important;
  &:hover {
    opacity: 0.9;
  }
`

const OutlineBtn = styled(ActionBtn)`
  background: #fff !important;
  border-color: ${T.border} !important;
  color: ${T.textPrimary} !important;
  &:hover {
    border-color: ${T.primary} !important;
    color: ${T.primary} !important;
  }
`

const RedOutlineBtn = styled(ActionBtn)`
  background: #fff !important;
  border-color: ${T.red} !important;
  color: ${T.red} !important;
  &:hover {
    background: #FFF5F5 !important;
  }
`

/* ───────── Status tag component ───────── */

const STATUS_CONFIG = {
  GENERATING: { color: T.primary, bg: '#F0F1FD', label: 'Generating', useCheck: false },
  REVIEW_PENDING: { color: '#B54708', bg: '#FFF7ED', label: 'Review Pending', useCheck: false },
  ALL_APPROVED: { color: '#027A48', bg: T.green80, label: 'All Approved', useCheck: true },
  ASSIGNING: { color: T.purple, bg: T.purpleBg, label: 'Assigning', useCheck: false },
  ASSIGNED: { color: T.textSecondary, bg: T.grey92, label: 'Assigned', useCheck: true },
  GENERATION_FAILED: { color: T.red, bg: '#FADFDF', label: 'Failed', useCheck: false },
}

const StatusTag = ({ status }) => {
  const c = STATUS_CONFIG[status] || STATUS_CONFIG.GENERATING
  return (
    <StatusBadge $bg={c.bg} $color={c.color}>
      {c.useCheck ? <CheckIcon /> : <StatusDot $color={c.color} />}
      {c.label}
    </StatusBadge>
  )
}

/* ───────── SVG icons ───────── */

const ArrowLeftIcon = () => (
  <svg viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M10 12L6 8L10 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
)

const ClipboardSearchIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M9 5H7C5.89543 5 5 5.89543 5 7V19C5 20.1046 5.89543 21 7 21H17C18.1046 21 19 20.1046 19 19V7C19 5.89543 18.1046 5 17 5H15" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
    <path d="M9 5C9 3.89543 9.89543 3 11 3H13C14.1046 3 15 3.89543 15 5C15 6.10457 14.1046 7 13 7H11C9.89543 7 9 6.10457 9 5Z" stroke="currentColor" strokeWidth="1.5"/>
    <circle cx="12" cy="14" r="3" stroke="currentColor" strokeWidth="1.5"/>
    <path d="M14.5 16.5L16 18" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
  </svg>
)

const EmptyDocIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M14 2H6C4.89543 2 4 2.89543 4 4V20C4 21.1046 4.89543 22 6 22H18C19.1046 22 20 21.1046 20 20V8L14 2Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <polyline points="14,2 14,8 20,8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <line x1="9" y1="13" x2="15" y2="13" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
    <line x1="9" y1="17" x2="13" y2="17" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
  </svg>
)

/* ───────── Progress bar color ───────── */

const getProgressColor = (status) => {
  switch (status) {
    case 'GENERATING':
      return T.primary
    case 'REVIEW_PENDING':
      return T.orange
    case 'ALL_APPROVED':
    case 'ASSIGNED':
      return T.green
    case 'GENERATION_FAILED':
      return T.red
    case 'ASSIGNING':
      return T.purple
    default:
      return T.primary
  }
}

/* ───────── Empty state component ───────── */

const NoGroupsFound = () => (
  <EmptyStateWrapper>
    <EmptyIcon>
      <EmptyDocIcon />
    </EmptyIcon>
    <EmptyTitle>No assessment groups found</EmptyTitle>
    <EmptyDesc>
      Assessment groups will appear here once assessments are generated.
    </EmptyDesc>
  </EmptyStateWrapper>
)

/* ───────── Entity name cell ───────── */

const EntityName = styled.div`
  font-size: 13px;
  font-weight: 500;
  color: ${T.textPrimary};
  max-width: 200px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

/* ───────── Main component ───────── */

const ReviewAssessmentsList = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const [entityType, setEntityType] = useState('college')
  const [groups, setGroups] = useState([])
  const [loading, setLoading] = useState(false)
  const [search, setSearch] = useState('')
  // Inline Assignment-Activity expansion (the merged "Assignment Activity" view).
  const [expandedKeys, setExpandedKeys] = useState([])
  // Merged page has two views on one page: role-based assessments (set-groups)
  // and assignment activity (all-type jobs). Both scoped to the entity tab.
  const [view, setView] = useState('assessments') // 'assessments' | 'activity'
  const [jobs, setJobs] = useState([])
  const [jobsTotal, setJobsTotal] = useState(0)
  const [jobsPage, setJobsPage] = useState(0)
  const [jobsLoading, setJobsLoading] = useState(false)
  const JOBS_PAGE_SIZE = 10
  const [page, setPage] = useState(0)
  const [total, setTotal] = useState(0)
  const PAGE_SIZE = 10

  const fetchGroups = useCallback(async () => {
    setLoading(true)
    try {
      const res = await getSetGroups({
        entityType,
        search: search || undefined,
        page,
        limit: PAGE_SIZE,
      })
      const data = res.data?.data || {}
      setGroups(data.groups || [])
      setTotal(data.total || 0)
    } catch (err) {
      console.error('Failed to fetch groups:', err)
      setGroups([])
      setTotal(0)
    }
    setLoading(false)
  }, [entityType, search, page])

  useEffect(() => {
    fetchGroups()
  }, [fetchGroups])

  // Reset page when tab or search changes
  useEffect(() => {
    setPage(0)
  }, [entityType, search])

  // Assignment-activity jobs (all types) for the "Activity" view, server-paginated.
  const fetchJobs = useCallback(async () => {
    setJobsLoading(true)
    try {
      const res = await getAssignments({ entityType, limit: JOBS_PAGE_SIZE, offset: jobsPage * JOBS_PAGE_SIZE })
      const data = res.data?.data || {}
      setJobs(data.jobs || [])
      setJobsTotal(data.total || 0)
    } catch (err) {
      setJobs([])
      setJobsTotal(0)
    }
    setJobsLoading(false)
  }, [entityType, jobsPage])

  useEffect(() => {
    if (view === 'activity') fetchJobs()
  }, [view, fetchJobs])

  useEffect(() => { setJobsPage(0) }, [entityType])

  // Deep-links from "View activity" links/emails:
  //  ?expandGroup=<id>[&entity=...]  → assessments view, expand that set-group row
  //  ?view=activity                  → activity view (the jobs list)
  //  ?view=activity&job=<id>         → straight to that job's real-time detail page
  //                                    (back-compat for older email links)
  useEffect(() => {
    const params = new URLSearchParams(location.search)
    const entity = params.get('entity')
    if (entity === 'college' || entity === 'corporate') setEntityType(entity)
    const gid = params.get('expandGroup')
    if (gid) { setView('assessments'); setExpandedKeys([gid]); return }
    if (params.get('view') === 'activity') {
      const jid = params.get('job')
      if (jid) { navigate(`/assessment/activity/job/${jid}`, { replace: true }); return }
      setView('activity')
    }
  }, [location.search])


  // Stable key for SSE — only reconnect when the set of generating group IDs changes
  const generatingKey = groups
    .filter(g => g.status === 'GENERATING' || g.status === 'ASSIGNING')
    .map(g => g.id)
    .sort()
    .join(',')

  // SSE subscription for groups that are generating
  useEffect(() => {
    if (!generatingKey) return

    const groupIds = generatingKey.split(',')
    const eventSources = groupIds.map(id => {
      const es = subscribeSSE(`assessment-generation:${id}`)
      es.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data)
          if (['SET_GENERATED', 'ALL_SETS_GENERATED', 'GENERATION_FAILED'].includes(data.type)) {
            fetchGroups()
          }
        } catch (e) {}
      }
      return es
    })

    return () => eventSources.forEach(es => es.close())
  }, [generatingKey, fetchGroups])

  const getActionButton = (group) => {
    const go = () => navigate(`/assessment/review/${group.id}`)
    // For broadcast groups: "Assign" becomes irrelevant — show Review or Details
    if (group.isBroadcast) {
      switch (group.status) {
        case 'GENERATING':
          return <OutlineBtn onClick={go}>View</OutlineBtn>
        case 'REVIEW_PENDING':
          return <PrimaryBtn onClick={go}>Review</PrimaryBtn>
        case 'ALL_APPROVED':
        case 'ASSIGNED':
          return <OutlineBtn onClick={go}>Details</OutlineBtn>
        case 'GENERATION_FAILED':
          return <RedOutlineBtn onClick={go}>Retry</RedOutlineBtn>
        default:
          return <OutlineBtn onClick={go}>View</OutlineBtn>
      }
    }
    switch (group.status) {
      case 'GENERATING':
        return <OutlineBtn onClick={go}>View</OutlineBtn>
      case 'REVIEW_PENDING':
        return <PrimaryBtn onClick={go}>Review</PrimaryBtn>
      case 'ALL_APPROVED':
        return <GreenBtn onClick={go}>Assign</GreenBtn>
      case 'ASSIGNED':
        return <OutlineBtn onClick={go}>Details</OutlineBtn>
      case 'GENERATION_FAILED':
        return <RedOutlineBtn onClick={go}>Retry</RedOutlineBtn>
      default:
        return <OutlineBtn onClick={go}>View</OutlineBtn>
    }
  }

  const columns = [
    {
      title: 'ASSESSMENT',
      key: 'role',
      render: (_, group) => (
        <RoleCell>
          <RoleName>
            {group.groupConfig?.name || group.generationPayload?.roleName || 'N/A'}
            {group.isBroadcast && (
              <span style={{ fontSize: 10, fontWeight: 600, padding: '1px 6px', marginLeft: 8, background: '#EAEDFB', color: '#3249D7', borderRadius: 4, verticalAlign: 'middle' }}>BROADCAST</span>
            )}
          </RoleName>
          <RoleMeta>
            {group.generationPayload?.roleName && (
              <>
                <span>{group.generationPayload.roleName}</span>
                <MetaDot />
              </>
            )}
            {group.generationPayload?.seniority && (
              <>
                <span>{group.generationPayload.seniority}</span>
                <MetaDot />
              </>
            )}
            <span>{timeAgo(group.createdAt)}</span>
          </RoleMeta>
        </RoleCell>
      ),
    },
    {
      title: 'ENTITY',
      key: 'entity',
      width: 220,
      render: (_, group) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <QuotaDonut entityId={group.entityId} />
          <EntityName title={group.entityName || '-'} style={{ flex: 1 }}>
            {group.entityName || '-'}
          </EntityName>
        </div>
      ),
    },
    {
      title: 'STUDENTS',
      dataIndex: 'totalStudents',
      key: 'students',
      width: 100,
      render: (val) => <StudentsCount>{val ?? '-'}</StudentsCount>,
    },
    {
      title: 'SETS',
      key: 'sets',
      width: 160,
      render: (_, group) => {
        const pct = group.totalSets ? Math.round((group.generatedSets / group.totalSets) * 100) : 0
        return (
          <SetsCell>
            <Progress
              percent={pct}
              size="small"
              showInfo={false}
              strokeColor={getProgressColor(group.status)}
              trailColor={T.grey92}
              style={{ width: 90 }}
            />
            <SetsLabel>{group.generatedSets}/{group.totalSets}</SetsLabel>
          </SetsCell>
        )
      },
    },
    {
      title: 'STATUS',
      key: 'status',
      width: 160,
      render: (_, group) => <StatusTag status={group.status} />,
    },
    {
      title: '',
      key: 'action',
      width: 110,
      align: 'right',
      render: (_, group) => getActionButton(group),
    },
  ]

  const JOB_STATE = {
    completed: { c: '#027A48', bg: T.green80, label: 'Completed' },
    completed_with_errors: { c: '#B54708', bg: '#FFF7ED', label: 'Completed · errors' },
    failed: { c: T.red, bg: '#FADFDF', label: 'Failed' },
  }
  const jobStateTag = (s) => {
    const cfg = JOB_STATE[s] || { c: T.primary, bg: '#F0F1FD', label: (s || 'running').replace(/_/g, ' ') }
    return <StatusBadge $bg={cfg.bg} $color={cfg.c}><StatusDot $color={cfg.c} />{cfg.label}</StatusBadge>
  }
  const jobsColumns = [
    { title: 'ASSESSMENT', key: 'name',
      render: (_, j) => <span style={{ fontWeight: 600, color: T.textPrimary }}>{j.name || j.assessmentType}</span> },
    { title: 'TYPE', dataIndex: 'assessmentType', key: 'type', width: 150,
      render: (t) => <Tag>{(t || '').replace(/_/g, ' ')}</Tag> },
    { title: 'ENTITY', key: 'entity',
      render: (_, j) => <span style={{ color: T.textSecondary }}>{j.entityName || j.entityType}</span> },
    { title: 'STUDENTS', dataIndex: 'total', key: 'total', width: 110,
      render: (v) => <StudentsCount>{v ?? '-'}</StudentsCount> },
    { title: 'FAILED', dataIndex: 'failed', key: 'failed', width: 90,
      render: (v) => <span style={{ fontWeight: 600, color: v ? T.red : T.textSecondary }}>{v ?? 0}</span> },
    { title: 'STATUS', dataIndex: 'state', key: 'state', width: 180, render: jobStateTag },
    { title: '', key: 'action', width: 110, align: 'right',
      render: (_, j) => <OutlineBtn onClick={(e) => { e.stopPropagation(); navigate(`/assessment/activity/job/${j.id}`, { state: { from: '/assessment/reviews?view=activity' } }) }}>Detail</OutlineBtn> },
  ]

  return (
    <PageWrapper>
      <HeaderCard>
        <BreadcrumbRow>
          <BackLink onClick={() => navigate('/assessment')}>
            <ArrowLeftIcon />
            Back to Assessments
          </BackLink>
        </BreadcrumbRow>
        <PageTitle>Manage Assessments</PageTitle>
        <TabContainer>
          <Tab $active={entityType === 'college'} onClick={() => { setEntityType('college'); setSearch('') }}>
            Institute
          </Tab>
          <Tab $active={entityType === 'corporate'} onClick={() => { setEntityType('corporate'); setSearch('') }}>
            Corporate
          </Tab>
        </TabContainer>
      </HeaderCard>

      <SelectorSection style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' }}>
        {view === 'assessments' ? (
          <Input.Search
            placeholder="Search by role name or entity name..."
            allowClear
            size="large"
            style={{ maxWidth: 420 }}
            onSearch={(val) => setSearch(val)}
            onChange={(e) => { if (!e.target.value) setSearch('') }}
          />
        ) : <span />}
        <Segmented
          value={view}
          onChange={setView}
          options={[
            { label: 'Assessments', value: 'assessments' },
            { label: 'Assignment Activity', value: 'activity' },
          ]}
        />
      </SelectorSection>

      <TableCard>
        {view === 'assessments' ? (
          <Spin spinning={loading}>
            <Table
              columns={columns}
              dataSource={groups}
              rowKey="id"
              expandable={{
                // Only assessments that have been assigned have activity to show.
                rowExpandable: (g) => g.status === 'ASSIGNING' || g.status === 'ASSIGNED',
                expandedRowKeys: expandedKeys,
                onExpandedRowsChange: (keys) => setExpandedKeys(keys),
                expandedRowRender: (g) => (
                  <AssignmentActivity key={g.id} groupId={g.id} embedded />
                ),
              }}
              pagination={
                total > PAGE_SIZE
                  ? {
                      current: page + 1,
                      pageSize: PAGE_SIZE,
                      total,
                      onChange: (p) => setPage(p - 1),
                      showSizeChanger: false,
                      style: { padding: '12px 20px', margin: 0 },
                    }
                  : false
              }
              locale={{
                emptyText: <NoGroupsFound />,
              }}
            />
          </Spin>
        ) : (
          <Spin spinning={jobsLoading}>
            <Table
              columns={jobsColumns}
              dataSource={jobs}
              rowKey="id"
              onRow={(j) => ({
                onClick: () => navigate(`/assessment/activity/job/${j.id}`, { state: { from: '/assessment/reviews?view=activity' } }),
                style: { cursor: 'pointer' },
              })}
              pagination={
                jobsTotal > JOBS_PAGE_SIZE
                  ? {
                      current: jobsPage + 1,
                      pageSize: JOBS_PAGE_SIZE,
                      total: jobsTotal,
                      onChange: (p) => setJobsPage(p - 1),
                      showSizeChanger: false,
                      style: { padding: '12px 20px', margin: 0 },
                    }
                  : false
              }
              locale={{ emptyText: <Empty description="No assignment activity yet" /> }}
            />
          </Spin>
        )}
      </TableCard>
    </PageWrapper>
  )
}

export default ReviewAssessmentsList
